import React, {useCallback, useEffect} from "react";
import Grid from "@material-ui/core/Grid";
import styled from "styled-components";
import {Colors} from "../../theme/colors";
import {ManuscriptDetailsModel} from "../../models/manuscript-details.model";
import {ManuscriptService} from "../../services/manuscript.service";
import {ProposalModel} from "../../models/proposal.model";
import {SubmittedModel} from "../../models/submitted.model";
import {showSnackSuccess, toggleLoader} from "../../store/global/global-slice";
import {useDispatch} from "react-redux";
import {ContentHeader} from "../../components/ui-base/content-header";
import {useHistory, useParams} from "react-router-dom";
import {BookDescriptionDialog} from "../../components/publisher/book-descriptions-dialog";
import {LeftBookDetails} from "../../components/shared/left-book-details";
import {Hidden, Tab, Tabs} from "@material-ui/core";
import Chip from "@material-ui/core/Chip";
import {CustomTabPanel} from "../../components/ui-base/tab-panel";
import {StyledButton} from "../../components/ui-base/button";
import {ApplicationToSend} from "../../components/author/books/application-to-send";
import {ApplicationSent} from "../../components/author/books/application-sent";
import {SuccessModal} from "../../components/ui-base/success-modal";
import {ConfirmDialog} from "../../components/confirm-dialog";
import {ApplicationStatusEnum} from "../../models/application-status.enum";
import {useTranslation} from "react-i18next";
import {BaseService} from "../../services/base.service";

const AuthorDetailsBookPage = () => {
    const [openDescriptionDialog, setOpenDescriptionDialog] = React.useState<boolean>(false);
    const [openConfirmDialog, setOpenConfirmDialog] = React.useState<boolean>(false);
    const [tabActive, setTabActive] = React.useState(0);
    const [manuscript, setManuscript] = React.useState<ManuscriptDetailsModel>(null);
    const [proposals, setProposals] = React.useState<ProposalModel[]>([]);
    const [submitted, setSubmitted] = React.useState<SubmittedModel[]>([]);
    const [toAccess, setToAccess] = React.useState<ProposalModel[]>([]);
    const [openSuccessModal, setOpenSuccessModal] = React.useState(false);
    const [successModalDesc, setSuccessModalDesc] = React.useState('');
    const history = useHistory();
    const dispatch = useDispatch();
    const urlParams = useParams();
    const { t } = useTranslation(['author', 'common']);
    const id: string = urlParams['id'] as string;

    const loadProposals = useCallback(async () => {
        dispatch(toggleLoader(true));
        const proposals = await ManuscriptService.getProposals(id, 0, 4, [ApplicationStatusEnum.Created, ApplicationStatusEnum.WithCont, ApplicationStatusEnum.Analyzed]);
        setProposals(proposals);

        const proposalsAskAccess = await ManuscriptService.getProposals(id, 0, 4, [ApplicationStatusEnum.AskAccess]);
        setToAccess(proposalsAskAccess);

        dispatch(toggleLoader(false));
    }, [id, dispatch])

    const loadSubmitted = useCallback(async () => {
        const submitted = await ManuscriptService.getSubmitted(id);

        setSubmitted(submitted);
    }, [id]);

    useEffect(() => {
        const loadManuscript = async (id: string) => {
            const manuscript = await ManuscriptService.getManuscript(id);

            setManuscript(manuscript);
            await loadProposals();
            await loadSubmitted();
        }

        if (id) {
            dispatch(toggleLoader(true));
            loadManuscript(id);
        }
    }, [id, dispatch, loadProposals, loadSubmitted]);

    const sortByMatching = (a: ProposalModel, b: ProposalModel) => {
        if (a.matching > b.matching) {
            return -1;
        } if (a.matching < b.matching) {
            return 1;
        }

        return 0;
    }

    const submitToPublisher = async (id: number, publisherActive: boolean) => {
        dispatch(toggleLoader(true));
        const submittedToPublisher = await ManuscriptService.submitToPublisher(id);

        if (submittedToPublisher) {
            setSuccessModalDesc((publisherActive) ? t('publishersBooks.sent1') : t('publishersBooks.sent2'));
            setOpenSuccessModal(true);
            await loadProposals();
            await loadSubmitted();
        } else {
            dispatch(toggleLoader(false));
        }
    }

    const confirmPublisher = async (id: number) => {
        dispatch(toggleLoader(true));
        const confirmedPublisher = await ManuscriptService.confirmPublisher(id);

        if (confirmedPublisher) {
            dispatch(showSnackSuccess(t('detailsManuscript.manuscriptPublished')));
            await loadProposals();
            await loadSubmitted();
        }

        dispatch(toggleLoader(false));
    }

    const declinePublisher = async (id: number) => {
        dispatch(toggleLoader(true));
        const confirmedPublisher = await ManuscriptService.declinePublisher(id);

        if (confirmedPublisher) {
            dispatch(showSnackSuccess(t('detailsManuscript.manuscriptNotPublished')));
            await loadProposals();
            await loadSubmitted();
        }

        dispatch(toggleLoader(false));
    }

    const deleteManuscript = async () => {
        dispatch(toggleLoader(true));
        const manuscriptDeleted = await ManuscriptService.deleteManuscript(manuscript.id);

        if (manuscriptDeleted) {
            dispatch(toggleLoader(false));
            history.push('/author/books');
        }
    }

    return (
        <StyledMainGrid container direction={"row"}>
            {manuscript &&
                <>
                    <BookDescriptionDialog
                        open={openDescriptionDialog}
                        onClose={(value) => {
                            setOpenDescriptionDialog(false);
                        }}
                        description={manuscript?.description}
                        summary={manuscript?.summary}
                        fragment={manuscript?.fragment}
                    />

                    <ConfirmDialog
                        open={openConfirmDialog}
                        onClose={(value) => {
                            setOpenConfirmDialog(false);

                            if (value) {
                                deleteManuscript();
                            }
                        }}
                        title={t('detailsManuscript.deleteTitle')}
                        text={t('detailsManuscript.deleteDesc')}
                    />

                    <Hidden mdDown>
                        <LeftBookDetails manuscript={manuscript} />
                    </Hidden>

                    <Grid item container direction={"column"} xs spacing={2} style={{overflow: 'hidden'}}>
                        <Grid item container direction={"column"} style={{position: 'relative'}}>
                            <Grid item style={{width: '100%'}}>
                                <StyledH6>{manuscript?.title}</StyledH6>
                            </Grid>
                            <Grid item>
                                <StyledDate>{t('common:created')}: {new Date(manuscript?.created).toLocaleString()}</StyledDate>
                            </Grid>

                            <Grid item>
                                <StyledDate>{t('common:updated')}: {new Date(manuscript?.updated).toLocaleString()}</StyledDate>
                            </Grid>

                            <StyledEditContainer>
                                <StyledButton type="submit" color={'violet'} onClick={() => {
                                    history.push('/author/edit-manuscript/' + manuscript?.id);
                                }} style={{marginRight: '0.5rem'}}>{t('common:edit')}</StyledButton>

                                <StyledButton type="submit" color={'red'} onClick={() => {
                                    setOpenConfirmDialog(true);
                                }}>{t('common:delete')}</StyledButton>
                            </StyledEditContainer>
                        </Grid>

                        <Grid item>
                            <ContentHeader title={t('detailsManuscript.detailsInfo')}/>
                        </Grid>

                        <Grid item container direction={"row"}>
                            <Grid item xs={12} md={6}>
                                <StyledSpan1>{t('common:categories')}:</StyledSpan1>
                                <Grid container direction={'row'} spacing={1}>
                                    {manuscript?.categories.map((category) => (
                                        <Grid item key={category.id} style={{maxWidth: '100%'}}>
                                            <StyledCategoryChip size="small" label={category.name} style={{maxWidth: '100%'}} />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <StyledSpan1>{t('common:tags')}:</StyledSpan1>
                                <Grid container direction={'row'} spacing={1}>
                                    {manuscript?.tags.filter((tag) => tag.tag_type === 'tag').map((tag) => (
                                        <Grid item key={tag.id} style={{maxWidth: '100%'}}>
                                            <StyledTagChip size="small" label={tag.name} style={{maxWidth: '100%'}}/>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item>
                            <Tabs value={tabActive} onChange={(event, newValue) => { setTabActive(newValue) }}>
                                <Tab label={t('common:bookDescriptionDialog.tabTitle1')} style={{fontSize: '0.9rem'}}/>
                                <Tab label={t('common:bookDescriptionDialog.tabTitle2')} style={{fontSize: '0.9rem'}}/>
                                <Tab label={t('common:bookDescriptionDialog.tabTitle3')} style={{fontSize: '0.9rem'}}/>
                            </Tabs>

                            <CustomTabPanel value={tabActive} index={0}>
                                {manuscript?.fragment &&
                                <StyledDescription>{BaseService.cutText(manuscript?.fragment, 1500)}</StyledDescription>
                                }

                                {!manuscript?.fragment &&
                                <StyledEmptyDescription>{t('detailsManuscript.fillPart')}</StyledEmptyDescription>
                                }

                                {manuscript?.fragment.length > 1500 &&
                                <StyledButton type={"button"} style={{marginTop: '1.5rem'}} color={"transparentViolet"} onClick={() => {
                                    setOpenDescriptionDialog(true);
                                }}>{t('detailsManuscript.showPart')}</StyledButton>
                                }
                            </CustomTabPanel>

                            <CustomTabPanel value={tabActive} index={1}>
                                {manuscript?.description &&
                                <StyledDescription>{BaseService.cutText(manuscript?.description, 1500)}</StyledDescription>
                                }

                                {!manuscript?.description &&
                                <StyledEmptyDescription>{t('detailsManuscript.fillDesc')}</StyledEmptyDescription>
                                }

                                {manuscript?.description.length > 1500 &&
                                <StyledButton type={"button"} style={{marginTop: '1.5rem'}} color={"transparentViolet"} onClick={() => {
                                    setOpenDescriptionDialog(true);
                                }}>{t('detailsManuscript.showDesc')}</StyledButton>
                                }
                            </CustomTabPanel>

                            <CustomTabPanel value={tabActive} index={2}>
                                {manuscript?.summary &&
                                <StyledDescription>{BaseService.cutText(manuscript?.summary, 1500)}</StyledDescription>
                                }

                                {!manuscript?.summary &&
                                <StyledEmptyDescription>{t('detailsManuscript.fillSummary')}</StyledEmptyDescription>
                                }

                                {manuscript?.summary.length > 1500 &&
                                <StyledButton type={"button"} style={{marginTop: '1.5rem'}} color={"transparentViolet"} onClick={() => {
                                    setOpenDescriptionDialog(true);
                                }}>{t('detailsManuscript.showSummary')}</StyledButton>
                                }
                            </CustomTabPanel>
                        </Grid>

                        <Grid item>
                            <ContentHeader title={t('detailsManuscript.sentManuscript')}/>
                        </Grid>

                         <Grid container item direction={'row'} spacing={1} justifyContent={"center"}>
                             {proposals.map((proposalData) =>
                                 <ApplicationToSend key={proposalData.id} proposal={proposalData} submitToPublisher={() => { submitToPublisher(proposalData.id, proposalData.publisher.publisher_type === 'ACTIVE') }}/>
                             )}
                         </Grid>

                        <Grid item container direction={"row"} justifyContent={"center"}>
                            <StyledButton type={"button"} color={"transparentViolet"} onClick={() => {
                                history.push('/author/publishers/' + manuscript?.id);
                            }}>{t('detailsManuscript.showAllPublishers')}</StyledButton>
                        </Grid>

                        <Grid item>
                            <ContentHeader title={t('detailsManuscript.applicationsSent')}/>
                        </Grid>

                        <Grid container item direction={'row'} spacing={1} justifyContent={"center"}>
                            {submitted.map((submittedData) =>
                                <ApplicationSent key={submittedData.id} application={submittedData} updateStatus={() => {loadSubmitted()}}/>
                            )}
                        </Grid>

                        {toAccess.length > 0 &&
                            <>
                            <Grid item>
                                <ContentHeader title={t('detailsManuscript.toAccessMarketTitle')}/>
                            </Grid>

                            <Grid container item direction={'row'} spacing={1} justifyContent={"center"}>
                                {toAccess.map((proposalData) =>
                                    <ApplicationToSend key={proposalData.id} proposal={proposalData} declinePublisher={() => {declinePublisher(proposalData.id)}} confirmPublisher={() => {confirmPublisher(proposalData.id)}}/>
                                )}
                            </Grid>
                            </>
                        }
                    </Grid>

                    <SuccessModal open={openSuccessModal} setOpen={setOpenSuccessModal} title={successModalDesc}/>
                </>
            }
        </StyledMainGrid>
    )
}

const StyledMainGrid = styled(Grid)`
  background-color: #fff;
  padding: 1rem;
  flex-wrap: nowrap;
`;

const StyledH6 = styled('h6')`
  font-size: 1.5rem;
  color: ${Colors.violet};
  font-weight: 600;
  margin-bottom: 0.9rem;
  max-width: 80%;
  overflow: hidden;
`;

const StyledDate = styled('p')`
  color: ${Colors.grayText};
  font-size: 0.8rem;
  font-weight: 400;
`;

const StyledSpan1 = styled('span')`
  font-weight: 500;
  font-size: 0.9rem;
  color: ${Colors.darkGray};
`;

const StyledCategoryChip = styled(Chip)`
  background-color: #FBF5FF;
  color: ${Colors.lightViolet};
  border: 1px solid ${Colors.lightViolet};
  font-weight: 500;
`;

const StyledTagChip = styled(Chip)`
  background-color: #F5F5F5;
  color: #424242;
  border: 1px solid #C2C2C2;
  font-weight: 500;
`;

const StyledDescription = styled('p')`
  font-size: 0.9rem;
  white-space: pre-line;
`;

const StyledEmptyDescription = styled('p')`
  font-size: 0.9rem;
  white-space: pre-line;
  color: ${Colors.grayText};
`;

const StyledEditContainer = styled('div')`
  position: absolute;
  right: 0;
`;

export default AuthorDetailsBookPage;
